import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Check, ChevronDown } from 'lucide-react';

// AUTO-319: shared controls for the workspace layer. Custom, so they look the
// same in every browser (a native <select> opens the OS menu).

export function WsSegmented({ label, items, value, onChange }) {
  const trackRef = useRef(null);
  const [thumb, setThumb] = useState(null);
  const measure = useCallback(() => {
    const active = trackRef.current?.querySelector('[aria-pressed="true"]');
    if (!active) return;
    const next = { left: active.offsetLeft, top: active.offsetTop, width: active.offsetWidth, height: active.offsetHeight };
    setThumb((previous) => (previous && Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next));
  }, []);
  useLayoutEffect(measure);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !trackRef.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(trackRef.current);
    return () => observer.disconnect();
  }, [measure]);
  return (
    <div ref={trackRef} className="ws-seg" role="group" aria-label={label}>
      {thumb && <span className="ws-seg-thumb" aria-hidden="true" style={{ width: thumb.width, height: thumb.height, transform: `translate(${thumb.left}px, ${thumb.top}px)` }} />}
      {items.map((item) => (
        <button key={item.id} type="button" className="ws-seg-btn" aria-pressed={value === item.id} onClick={() => onChange(item.id)}>
          {item.icon && <item.icon size={15} strokeWidth={2} aria-hidden="true" />}
          {item.label}
          {item.count !== undefined && item.count !== null && <span className="ws-seg-count ws-num">{item.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function WsDropdown({ label, value, options, onChange, icon: Icon, align = 'left' }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value));
  const current = options[selectedIndex];

  useEffect(() => {
    if (!open) return undefined;
    setActive(selectedIndex);
    listRef.current?.focus();
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => { setOpen(false); buttonRef.current?.focus(); };
  const choose = (option) => { close(); if (option.value !== value) onChange(option.value); };
  const onListKey = (event) => {
    const last = options.length - 1;
    const moves = { ArrowDown: Math.min(last, active + 1), ArrowUp: Math.max(0, active - 1), Home: 0, End: last };
    if (event.key in moves) { event.preventDefault(); setActive(moves[event.key]); }
    else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(options[active]); }
    else if (event.key === 'Escape') { event.preventDefault(); close(); }
    else if (event.key === 'Tab') setOpen(false);
  };

  return (
    <div ref={rootRef} className="ws-dd-root">
      <button
        ref={buttonRef}
        type="button"
        className="ws-dd"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current?.label || ''}`}
        onClick={() => setOpen((state) => !state)}
        onKeyDown={(event) => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); } }}
      >
        {Icon && <span className="ws-dd-tile" aria-hidden="true"><Icon size={15} strokeWidth={2.1} /></span>}
        <span className="ws-dd-key">{label}</span>
        <span className="ws-dd-value">{current?.short || current?.label}</span>
        <ChevronDown size={15} aria-hidden="true" className="ws-dd-chev" />
      </button>
      {open && (
        <div
          ref={listRef}
          className={`ws-dd-menu${align === 'right' ? ' is-right' : ''}`}
          role="listbox"
          aria-label={label}
          tabIndex={-1}
          aria-activedescendant={`${id}-${active}`}
          onKeyDown={onListKey}
        >
          <div className="ws-menu-label" aria-hidden="true">{label}</div>
          {options.map((option, index) => (
            <div
              key={option.value}
              id={`${id}-${index}`}
              role="option"
              aria-selected={option.value === value}
              className={`ws-dd-option${index === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(option)}
            >
              {option.dot && <span className={`ws-dd-dot ws-status--${option.dot}`} aria-hidden="true" />}
              <span className="ws-grow">{option.label}</span>
              {option.value === value && <Check size={15} aria-hidden="true" className="ws-dd-check" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Sort as a small popover: pick what to sort by, then the order. Changes apply
// immediately; the popover stays open until Escape or a click outside.
export function WsSortMenu({ fields, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [alignLeft, setAlignLeft] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [key, direction] = String(value).split('_');
  const field = fields.find((item) => item.key === key) || fields[0];
  const order = field.orders[direction] ? direction : field.defaultOrder;
  const DirectionIcon = order === 'asc' ? ArrowUpNarrowWide : ArrowDownWideNarrow;

  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.querySelector('[aria-checked="true"]')?.focus();
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') { event.preventDefault(); setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const moveWithin = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const group = event.currentTarget;
    const radios = [...group.querySelectorAll('[role="radio"]')];
    const index = radios.indexOf(document.activeElement);
    if (index < 0) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    radios[(index + step + radios.length) % radios.length].click();
    radios[(index + step + radios.length) % radios.length].focus();
  };

  return (
    <div ref={rootRef} className="ws-dd-root">
      <button ref={buttonRef} type="button" className="ws-sort" aria-haspopup="dialog" aria-expanded={open}
        aria-label={`Sort: ${field.label}, ${field.orders[order]}`}
        onClick={() => {
          // Open towards the side with room (the controls wrap on narrow screens).
          const rect = buttonRef.current?.getBoundingClientRect();
          setAlignLeft(Boolean(rect && rect.right - 240 < 12));
          setOpen((state) => !state);
        }}>
        <DirectionIcon size={15} strokeWidth={2} aria-hidden="true" className="ws-sort-icon" />
        <span>{field.label}</span>
        <ChevronDown size={14} aria-hidden="true" className="ws-dd-chev" />
      </button>
      {open && (
        <div ref={panelRef} className={`ws-sort-panel${alignLeft ? ' is-left' : ''}`} role="dialog" aria-label="Sort agents">
          <div className="ws-menu-label" id="ws-sort-by">Sort by</div>
          <div role="radiogroup" aria-labelledby="ws-sort-by" onKeyDown={moveWithin}>
            {fields.map((item) => {
              const Icon = item.icon;
              const checked = item.key === field.key;
              return (
                <button key={item.key} type="button" role="radio" aria-checked={checked} tabIndex={checked ? 0 : -1}
                  className="ws-sort-option" onClick={() => { if (!checked) onChange(`${item.key}_${item.defaultOrder}`); }}>
                  {Icon && <Icon size={15} aria-hidden="true" />}
                  <span className="ws-grow">{item.label}</span>
                  {checked && <Check size={15} aria-hidden="true" className="ws-dd-check" />}
                </button>
              );
            })}
          </div>
          <div className="ws-menu-sep" />
          <div className="ws-menu-label" id="ws-sort-order">Order</div>
          <div className="ws-sort-order" role="radiogroup" aria-labelledby="ws-sort-order" onKeyDown={moveWithin}>
            {Object.entries(field.orders).map(([id, label]) => {
              const Icon = id === 'asc' ? ArrowUpNarrowWide : ArrowDownWideNarrow;
              const checked = id === order;
              return (
                <button key={id} type="button" role="radio" aria-checked={checked} tabIndex={checked ? 0 : -1}
                  onClick={() => { if (!checked) onChange(`${field.key}_${id}`); }}>
                  <Icon size={14} aria-hidden="true" />{label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
